import React, { useEffect, useRef, useState } from "react";
import { FaGithub, FaLinkedin } from "react-icons/fa6";
import { FiChevronLeft, FiChevronRight, FiMail } from "react-icons/fi";

const Arrow = () => <span aria-hidden="true">↗</span>;

const projects = [
  {
    number: "01",
    title: "BLE Attendee Tracking",
    eyebrow: "Real-time event intelligence",
    description: "A location-aware attendance platform that turns Bluetooth signals into useful, privacy-conscious insights for busy physical events.",
    tags: ["Go", "Ruby on Rails", "React", "Bluetooth LE", "Google BigQuery", "GCP"],
    imageKey: "mobileApps",
    accent: "coral"
  },
  {
    number: "02",
    title: "Event Platform",
    eyebrow: "SaaS · Events at scale",
    description: "A flexible event platform spanning registration, content, live experiences, and the operational tools teams need behind the scenes.",
    tags: ["Ruby on Rails", "React", "PostgreSQL", "Mixpanel", "Mux", "SaaS", "WYSIWYG", "GCP"],
    imageKey: "vrApps",
    accent: "teal"
  },
  {
    number: "03",
    title: "Operational Tools & Commerce",
    eyebrow: "Complexity made useful",
    description: "Purpose-built workflows for inventory, commerce, and event operations—designed to make complicated work feel clear and dependable.",
    tags: ["Systems design", "PostgreSQL", "APIs", "UX engineering"],
    imageKey: "digitalTools",
    accent: "gold"
  }
];

const skillGroups = [
  ["Languages", ["Ruby", "JavaScript", "Python", "SQL", "Go"]],
  ["Product stack", ["Ruby on Rails", "React", "Django", "MUI", "REST APIs"]],
  ["Data & cloud", ["PostgreSQL", "BigQuery", "GCP", "Docker", "Heroku"]],
  ["Engineering", ["SaaS architecture", "RBAC", "Code review", "Monitoring", "Agile delivery"]]
];

function useReveal() {
  useEffect(() => {
    const items = document.querySelectorAll("[data-reveal]");
    if (!("IntersectionObserver" in window)) {
      items.forEach(item => item.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    items.forEach(item => observer.observe(item));
    return () => observer.disconnect();
  }, []);
}

const bleViews = [
  { key: "bleHeatmap", label: "Heatmap", metric: "4 Zones", alt: "Event floor plan with attendee location heatmap and BLE edge stations" },
  { key: "bleCoverage", label: "Coverage", metric: "15 beacons", alt: "BLE beacon coverage visualization over an event floor plan" },

  { key: "bleAttendance", label: "Attendance", metric: "294 Attendees", alt: "Live triangulation attendance graph showing attendee volume over time" },
  
];

const BleVisual = ({ images }) => {
  const [activeView, setActiveView] = useState(0);
  const view = bleViews[activeView];

  return (
    <div className="ble-visual">
      <div className="ble-visual__topline">
        <span><i /> Telemetry</span>
        <strong>{view.metric}</strong>
      </div>
      <div className="ble-visual__canvas">
        {bleViews.map((item, index) => (
          <img
            className={index === activeView ? "is-active" : ""}
            src={images[item.key]}
            alt={item.alt}
            loading={index === 0 ? "eager" : "lazy"}
            key={item.key}
          />
        ))}
        <div className="ble-visual__scan" aria-hidden="true" />
        
      </div>
      <div className="ble-visual__tabs" role="tablist" aria-label="BLE project visualizations">
        {bleViews.map((item, index) => (
          <button
            type="button"
            role="tab"
            aria-selected={index === activeView}
            className={index === activeView ? "is-active" : ""}
            onClick={() => setActiveView(index)}
            key={item.key}
          >
            <span>0{index + 1}</span>{item.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const growlViews = [
  { key: "silentgrowlEventSite", label: "Attendee experience", kicker: "Branded event sites", alt: "GrowlFest attendee-facing event website" },
];

const SilentGrowlVisual = ({ images }) => {
  const [activeView, setActiveView] = useState(0);
  const setRelativeView = direction => setActiveView(current => (current + direction + growlViews.length) % growlViews.length);

  return (
    <div className="growl-carousel">
      <div className="growl-carousel__slides">
        {growlViews.map((view, index) => (
          <img className={index === activeView ? "is-active" : ""} src={images[view.key]} alt={view.alt} loading="lazy" key={view.key} />
        ))}
      </div>
      <div className="growl-carousel__caption">
        <span>0{activeView + 1} / 0{growlViews.length}</span>
        <div><small>{growlViews[activeView].kicker}</small><strong>{growlViews[activeView].label}</strong></div>
      </div>
      {/*
      <div className="growl-carousel__controls">
        <button type="button" onClick={() => setRelativeView(-1)} aria-label="Previous SilentGrowl screenshot"><FiChevronLeft /></button>
        <button type="button" onClick={() => setRelativeView(1)} aria-label="Next SilentGrowl screenshot"><FiChevronRight /></button>
      </div>
      */}
      <div className="growl-carousel__dots" aria-hidden="true">
        {growlViews.map((view, index) => <i className={index === activeView ? "is-active" : ""} key={view.key} />)}
      </div>
    </div>
  );
};

const ProjectCard = ({ project, image, images }) => (
  <article className={`work-card work-card--${project.accent}`} data-reveal>
    <div className="work-card__visual">
      {project.number === "01" && <BleVisual images={images} />}
      {project.number === "02" && <SilentGrowlVisual images={images} />}
      {project.number === "03" && <img src={image} alt="" loading="lazy" />}
    </div>
    <div className="work-card__content">
      <p className="eyebrow">{project.eyebrow}</p>
      <h3>{project.title}</h3>
      <p className="work-card__description">{project.description}</p>
      <ul className="tag-list" aria-label="Technologies and disciplines">
        {project.tags.map(tag => <li key={tag}>{tag}</li>)}
      </ul>
    </div>
  </article>
);



function IndexProjectList({ images }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerCompact, setHeaderCompact] = useState(false);
  const glowRef = useRef(null);
  useReveal();

  useEffect(() => {
    const moveGlow = event => {
      if (glowRef.current) {
        glowRef.current.style.transform = `translate3d(${event.clientX - 240}px, ${event.clientY - 240}px, 0)`;
      }
    };
    window.addEventListener("pointermove", moveGlow, { passive: true });
    return () => window.removeEventListener("pointermove", moveGlow);
  }, []);

  useEffect(() => {
    const updateHeader = () => setHeaderCompact(window.scrollY > 48);
    updateHeader();
    window.addEventListener("scroll", updateHeader, { passive: true });
    return () => window.removeEventListener("scroll", updateHeader);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="portfolio-site">
      <div className="cursor-glow" ref={glowRef} aria-hidden="true" />
      <header className={headerCompact ? "site-nav is-compact" : "site-nav"}>
        <a href="#top" className="wordmark" aria-label="Jory Leech, home">
          <span className="wordmark__signature"><img src={images.logo} alt="" /></span>
          <span className="wordmark__name">Jory Leech</span>
        </a>
        <button className="menu-toggle" type="button" aria-expanded={menuOpen} aria-controls="primary-nav" onClick={() => setMenuOpen(!menuOpen)}>
          <span /> <span />
          <span className="sr-only">Toggle navigation</span>
        </button>
        <nav id="primary-nav" className={menuOpen ? "nav-links is-open" : "nav-links"} aria-label="Primary navigation">
          <a href="#resume" onClick={closeMenu}>Résumé</a>
          <a href="#work" onClick={closeMenu}>Projects</a>
          <a href="/projects" onClick={closeMenu}>Personal work</a>
          <a className="nav-links__contact" href="mailto:joryleech@gmail.com">Let’s talk <Arrow /></a>
        </nav>
      </header>

      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__copy">
            <div className="availability" data-reveal><span /> Portland, Oregon · Open to remote opportunities</div>
            <h1 id="hero-title" data-reveal>
              Senior full-stack<br /><em>software engineer.</em>
            </h1>
            <div className="hero__footer" data-reveal>
              <p>Eight years building and leading dependable SaaS platforms with Ruby on Rails, React, Go, PostgreSQL, BigQuery, and GCP—from architecture through production ownership.</p>
              <a className="text-link" href="#work">Explore selected work <span>↓</span></a>
            </div>
          </div>
          <div className="hero__portrait" data-reveal>
            <div className="portrait-frame">
              <img src={images.portrait} alt="Jory Leech working alongside a robot sculpture" />
              <div className="portrait-frame__label">Based in the Pacific Northwest<br />Building for everywhere</div>
              <div className="portrait-frame__socials" aria-label="Social and contact links">
                <a href="https://github.com/joryleech" target="_blank" rel="noreferrer"><FaGithub aria-hidden="true" /> <span>GitHub</span></a>
                <a href="https://www.linkedin.com/in/jory-leech-3b3123133/" target="_blank" rel="noreferrer"><FaLinkedin aria-hidden="true" /> <span>LinkedIn</span></a>
                <a href="mailto:joryleech@gmail.com"><FiMail aria-hidden="true" /> <span>Email</span></a>
              </div>
            </div>
          </div>
        </section>

        <section className="proof-strip" aria-label="Professional highlights">
          <div><strong>8+</strong><span>Years turning ideas<br />into working software</span></div>
          <div><strong>Full stack</strong><span>From data model<br />to interface detail</span></div>
          <div><strong>Human first</strong><span>Systems designed around<br />the people using them</span></div>
        </section>

        
        <section className="resume-section" id="resume">
          <div className="resume-section__header section-shell" data-reveal>
            <div>
              <p className="eyebrow">Experience & expertise</p>
              <h2>Built through<br /><em>real responsibility.</em></h2>
            </div>
            <div className="resume-section__summary">
              <p>Eight years delivering SaaS products, live-event technology, internal platforms, and customer-facing applications—from first implementation through production ownership.</p>
              <a className="resume-download" href="/Jory-Leech-Resume.pdf" download aria-label="Download Jory Leech's software engineering résumé">
                Download résumé <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>

          <div className="resume-card section-shell" data-reveal>
            <div className="resume-card__meta">
              <p className="eyebrow">November 2018—Present</p>
              <span>Remote · Portland, OR</span>
            </div>
            <div className="resume-card__role">
              <p>Jaguar Design Studio</p>
              <h3>Senior Full Stack Developer</h3>
              <span>Advanced from Junior to Senior</span>
            </div>
            <div className="resume-card__highlights">
              <p>Built and maintained SilentGrowl for <strong>100+ programs</strong> and audiences of <strong>30,000+ attendees.</strong></p>
              <p>Led enterprise event technology delivery for <strong>Cisco, DocuSign, and Sumo Logic.</strong></p>
              <p>Developed real-time BLE attendee tracking with <strong>Go, BigQuery, and React.</strong></p>
              <p>Managed junior engineers across planning, review, deployment, and live delivery.</p>
            </div>
          </div>

          <div className="skills-grid section-shell">
            {skillGroups.map(([title, skills]) => (
              <article key={title} data-reveal>
                <p className="eyebrow">{title}</p>
                <ul>{skills.map(skill => <li key={skill}>{skill}</li>)}</ul>
              </article>
            ))}
          </div>

          <div className="education-row section-shell" data-reveal>
            <span className="eyebrow">Education</span>
            <div><strong>B.S. Computer Science</strong><span>University of Hawaii at Manoa · Cum Laude · 3.8 GPA</span></div>
            <span>2018</span>
          </div>
        </section>


        <section className="work-section section-shell" id="work">
          <div className="section-heading" data-reveal>
            <h2>Products built for<br /><em>real-world pressure.</em></h2>
            <p>A selection of systems where product judgment, technical depth, and calm execution mattered equally.</p>
          </div>
          <div className="client-strip" data-reveal aria-label="Selected enterprise client work">
            <p className="eyebrow">Trusted to deliver for</p>
            <div className="client-strip__names">
              <span>Cisco</span>
              <span>Sumo Logic</span>
              <span>DocuSign</span>
            </div>
            <p>Enterprise event technology built for high-pressure live and digital programs.</p>
          </div>
          <div className="work-grid">
            {projects.map(project => <ProjectCard key={project.number} project={project} image={images[project.imageKey]} images={images} />)}
          </div>
        </section>


        <section className="contact-section">
          <div className="contact-section__inner section-shell" data-reveal>
            <p className="eyebrow">Have a difficult problem?</p>
            <h2>Let’s build something<br /><em>worth keeping.</em></h2>
            <a href="mailto:joryleech@gmail.com" className="contact-button">Start a conversation <Arrow /></a>
          </div>
        </section>
      </main>

      <footer className="site-footer section-shell">
        <a className="wordmark" href="#top"><span className="wordmark__signature"><img src={images.logo} alt="" /></span><span className="wordmark__name">Jory Leech</span></a>
        <p>Senior software engineer<br />Product-minded systems builder</p>
        <div><a href="/projects">Personal projects <Arrow /></a><a href="https://www.linkedin.com/in/jory-leech-3b3123133/" rel="noreferrer">LinkedIn <Arrow /></a><a href="https://github.com/joryleech" rel="noreferrer">GitHub <Arrow /></a></div>
        <small>© {new Date().getFullYear()} Jory Leech</small>
      </footer>
    </div>
  );
}

export default IndexProjectList;
